<template>
  <header class="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-100/50">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between py-4">
        <!-- Logo -->
        <div class="flex-shrink-0">
          <a href="/">
            <img src="https://cdn.arrify.com/wp-content/uploads/2022/05/cropped-Arrify_Text_small.png" alt="Arrify Logo" class="h-8 w-auto">
          </a>
        </div>

        <!-- Desktop menu and CTA -->
        <div class="hidden md:flex items-center space-x-8">
          <!-- Desktop menu -->
          <nav class="flex items-center space-x-8">
          <template v-for="(item, index) in navigationItems" :key="index">
            <!-- Item without children -->
            <a
              v-if="!item.children"
              :href="item.path"
              class="text-base font-medium text-gray-600 hover:text-blue-600 transition-colors duration-200"
              :class="{ 'text-blue-600 font-semibold': isActiveRoute(item.path) }"
            >
              {{ item.label }}
            </a>

            <!-- Item with children (dropdown) -->
            <div
              v-else
              class="relative"
              @mouseenter="openDropdown(index)"
              @mouseleave="closeDropdown(index)"
            >
              <button
                class="text-base font-medium text-gray-600 hover:text-blue-600 flex items-center transition-colors duration-200"
                :class="{ 'text-blue-600 font-semibold': isActiveRoute(item.path) }"
                @click="toggleDropdown(index)"
                :aria-expanded="openDropdownIndex === index"
              >
                {{ item.label }}
                <svg
                  class="ml-1 h-4 w-4 transition-transform duration-200"
                  :class="{ 'rotate-180': openDropdownIndex === index }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>

              <!-- Dropdown menu -->
              <div
                v-show="openDropdownIndex === index"
                class="absolute z-50 mt-3 w-52 rounded-lg shadow-xl bg-white ring-1 ring-gray-200 border border-gray-100"
              >
                <div class="py-2" role="menu">
                  <a
                    v-for="(child, childIndex) in item.children"
                    :key="childIndex"
                    :href="child.path"
                    class="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                    :class="{ 'bg-blue-50 text-blue-700 font-medium': isActiveRoute(child.path) }"
                    role="menuitem"
                  >
                    {{ child.label }}
                  </a>
                </div>
              </div>
            </div>
          </template>
        </nav>

        <!-- CTA Button (Desktop) -->
        <a
          href="/contact"
          class="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
        >
          Get Started
        </a>
      </div>

      <!-- Mobile menu button -->
      <div class="md:hidden flex items-center">
        <button
          @click="toggleMenu"
          class="text-gray-600 hover:text-blue-600 focus:outline-none focus:text-blue-600 transition-colors duration-200"
          aria-label="Toggle menu"
          aria-expanded="isMenuOpen"
        >
          <svg class="h-6 w-6 fill-current" viewBox="0 0 24 24">
            <path v-if="!isMenuOpen" fill-rule="evenodd" clip-rule="evenodd" d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"/>
            <path v-else fill-rule="evenodd" clip-rule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"/>
          </svg>
        </button>
      </div>
    </div>
    </div>

    <!-- Mobile menu, show/hide based on menu state -->
    <div v-if="isMenuOpen" class="md:hidden border-t border-gray-100 bg-white">
      <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        <template v-for="(item, index) in navigationItems" :key="index">
          <!-- Item without children -->
          <a
            v-if="!item.children"
            :href="item.path"
            class="block px-3 py-2.5 rounded-lg text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
            :class="{ 'bg-blue-50 text-blue-600 font-semibold': isActiveRoute(item.path) }"
          >
            {{ item.label }}
          </a>

          <!-- Item with children (accordion) -->
          <div v-else class="space-y-1">
            <button
              @click="toggleMobileSubmenu(index)"
              class="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
              :class="{ 'bg-blue-50 text-blue-600 font-semibold': isActiveRoute(item.path) }"
              :aria-expanded="openMobileSubmenuIndex === index"
            >
              <span>{{ item.label }}</span>
              <svg
                class="h-5 w-5 transition-transform duration-200"
                :class="{ 'rotate-180': openMobileSubmenuIndex === index }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>

            <!-- Submenu items -->
            <div
              v-show="openMobileSubmenuIndex === index"
              class="pl-4 space-y-1"
            >
              <a
                v-for="(child, childIndex) in item.children"
                :key="childIndex"
                :href="child.path"
                class="block px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                :class="{ 'bg-blue-50 text-blue-600 font-semibold': isActiveRoute(child.path) }"
              >
                {{ child.label }}
              </a>
            </div>
          </div>
        </template>

        <!-- CTA Button (Mobile) -->
        <div class="pt-4 pb-2">
          <a
            href="/contact"
            class="block text-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 shadow-md hover:shadow-lg transition-all duration-200"
          >
            Get Started
          </a>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { navigationItems } from '../config/navigation.js'

export default {
  name: 'HeaderComponent',
  data() {
    return {
      isMenuOpen: false,
      openDropdownIndex: null,
      openMobileSubmenuIndex: null,
      navigationItems: navigationItems,
      currentPath: '',
      closeTimeout: null
    }
  },
  mounted() {
    this.currentPath = window.location.pathname

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
      if (!this.isMenuOpen) {
        this.openMobileSubmenuIndex = null
      }
    },
    openDropdown(index) {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout)
        this.closeTimeout = null
      }
      this.openDropdownIndex = index
    },
    closeDropdown(index) {
      // Add a delay before closing to allow mouse to move to submenu
      this.closeTimeout = setTimeout(() => {
        if (this.openDropdownIndex === index) {
          this.openDropdownIndex = null
        }
      }, 200) // 200ms delay
    },
    toggleDropdown(index) {
      this.openDropdownIndex = this.openDropdownIndex === index ? null : index
    },
    toggleMobileSubmenu(index) {
      this.openMobileSubmenuIndex = this.openMobileSubmenuIndex === index ? null : index
    },
    isActiveRoute(path) {
      // Exact match for home
      if (path === '/') {
        return this.currentPath === '/'
      }
      // For other routes, check if current path starts with the item path
      return this.currentPath.startsWith(path)
    },
    handleClickOutside(event) {
      const header = this.$el
      if (header && !header.contains(event.target)) {
        this.openDropdownIndex = null
      }
    }
  }
}
</script>

<style scoped>
/* Ensure dropdowns appear above other content */
.relative {
  position: relative;
}

/* Smooth transitions for dropdowns and accordions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.transition-colors {
  transition-property: color, background-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
